import { fetchOrder, type OrderResponseV2, ResourceType } from '@library/api-order'
import {
  type DashboardExperienceOrderDeliveryDetailsDay,
  type DashboardExperienceOrderDeliveryDetailsResponse,
  type DashboardExperienceOrderResponse,
  type UpdateDashboardExperienceOrderRequest,
} from '@library/api-dashboard-experience'
import React, { useEffect } from 'react'

import { useOrderTileTelemetryTracking } from './useOrderTileTelemetryTracking'
import { useOrderTracking } from './useOrderTracking'
import { useUpdateDashboardOrder } from './useUpdateDashboardOrder'

type UseOrderTileDeliveryDateParams = {
  displayedOrder: DashboardExperienceOrderResponse
  onDeliveryDateSaved?: (selectedDeliveryDate: string) => void
  order: DashboardExperienceOrderResponse
  orderDeliveryDetails: DashboardExperienceOrderDeliveryDetailsResponse | undefined
}

type UseOrderTileDeliveryDateResult = {
  handleSaveDeliveryDate: (params: { date: string; formattedDate: string }) => Promise<void>
  hasSaveDeliveryDateError: boolean
  isSavingDeliveryDate: boolean
}

const resolveDeliveryDayId = ({
  displayedOrder,
  selectedDeliveryDate,
  selectedDeliveryDay,
}: {
  displayedOrder: DashboardExperienceOrderResponse
  selectedDeliveryDate: string
  selectedDeliveryDay: DashboardExperienceOrderDeliveryDetailsDay
}): string => {
  const snakeCaseCoreDayId = (selectedDeliveryDay as { core_day_id?: string }).core_day_id
  const isCurrentOrderDeliveryDate = selectedDeliveryDate === displayedOrder.analytics.delivery_date

  return (
    selectedDeliveryDay.coreDayId ||
    snakeCaseCoreDayId ||
    (isCurrentOrderDeliveryDate ? displayedOrder.analytics.delivery_day_id : '') ||
    selectedDeliveryDay.id ||
    ''
  )
}

const resolveSelectedDeliverySlot = (
  selectedDeliveryDay: DashboardExperienceOrderDeliveryDetailsDay,
) => selectedDeliveryDay.slots.find((slot) => slot.isDefault) || selectedDeliveryDay.slots[0]

const resolveDeliverySlotId = ({
  displayedOrder,
  selectedDeliveryDate,
  selectedDeliveryDay,
}: {
  displayedOrder: DashboardExperienceOrderResponse
  selectedDeliveryDate: string
  selectedDeliveryDay: DashboardExperienceOrderDeliveryDetailsDay
}): string => {
  const isCurrentOrderDeliveryDate = selectedDeliveryDate === displayedOrder.analytics.delivery_date
  const selectedDeliverySlot = resolveSelectedDeliverySlot(selectedDeliveryDay)

  if (!selectedDeliverySlot) {
    return isCurrentOrderDeliveryDate ? displayedOrder.analytics.delivery_slot_id : ''
  }

  const snakeCaseCoreSlotId = (selectedDeliverySlot as { core_slot_id?: string }).core_slot_id

  return (
    selectedDeliverySlot.coreSlotId ||
    snakeCaseCoreSlotId ||
    (isCurrentOrderDeliveryDate ? displayedOrder.analytics.delivery_slot_id : '') ||
    selectedDeliverySlot.id ||
    ''
  )
}

const updateIncludedResource = <
  TResource extends {
    id: string
    type: string
    attributes?: Record<string, unknown>
  },
>(
  included: TResource[],
  nextResource: TResource,
) => {
  const existingResourceIndex = included.findIndex(
    (resource) => resource.type === nextResource.type && resource.id === nextResource.id,
  )

  if (existingResourceIndex >= 0) {
    const nextIncluded = [...included]

    nextIncluded[existingResourceIndex] = {
      ...nextIncluded[existingResourceIndex],
      ...nextResource,
      attributes: {
        ...nextIncluded[existingResourceIndex].attributes,
        ...nextResource.attributes,
      },
    }

    return nextIncluded
  }

  const sameTypeResourceIndex = included.findIndex(
    (resource) => resource.type === nextResource.type,
  )

  if (sameTypeResourceIndex >= 0) {
    const nextIncluded = [...included]

    nextIncluded[sameTypeResourceIndex] = {
      ...nextIncluded[sameTypeResourceIndex],
      ...nextResource,
      attributes: {
        ...nextIncluded[sameTypeResourceIndex].attributes,
        ...nextResource.attributes,
      },
    }

    return nextIncluded
  }

  return [...included, nextResource]
}

const buildUpdateOrderRequestBody = ({
  displayedOrder,
  fullOrder,
  selectedDeliveryDay,
  selectedDeliveryDate,
}: {
  displayedOrder: DashboardExperienceOrderResponse
  fullOrder: OrderResponseV2
  selectedDeliveryDay: DashboardExperienceOrderDeliveryDetailsDay
  selectedDeliveryDate: string
}): UpdateDashboardExperienceOrderRequest => {
  const selectedDeliverySlot = resolveSelectedDeliverySlot(selectedDeliveryDay)
  const relationships = fullOrder.data.relationships ?? {}
  const existingDeliverySlot = relationships.delivery_slot?.data
  const resolvedDeliverySlotId = resolveDeliverySlotId({
    displayedOrder,
    selectedDeliveryDate,
    selectedDeliveryDay,
  })
  const resolvedDeliveryDayId = resolveDeliveryDayId({
    displayedOrder,
    selectedDeliveryDate,
    selectedDeliveryDay,
  })
  const resolvedDaySlotLeadTimeId =
    selectedDeliverySlot?.daySlotLeadTimeId || relationships.day_slot_lead_time?.data.id
  const includedWithDeliverySlot = updateIncludedResource(fullOrder.included, {
    id: resolvedDeliverySlotId,
    type: ResourceType.DeliverySlot,
    attributes: {
      core_id: selectedDeliverySlot?.coreSlotId || resolvedDeliverySlotId,
      delivery_slot_id: selectedDeliverySlot?.coreSlotId || resolvedDeliverySlotId,
      start: selectedDeliverySlot?.deliveryStartTime || '',
      end: selectedDeliverySlot?.deliveryEndTime || '',
    },
  })
  const includedWithDeliveryDay = updateIncludedResource(includedWithDeliverySlot, {
    id: resolvedDeliveryDayId,
    type: ResourceType.DeliveryDay,
    attributes: {
      date: `${selectedDeliveryDate}T00:00:00Z`,
    },
  })
  const updatedIncluded =
    resolvedDaySlotLeadTimeId === undefined
      ? includedWithDeliveryDay
      : updateIncludedResource(includedWithDeliveryDay, {
          id: resolvedDaySlotLeadTimeId,
          type: ResourceType.DaySlotLeadTime,
        })

  return {
    ...fullOrder,
    data: {
      ...fullOrder.data,
      relationships: {
        ...relationships,
        delivery_slot: {
          data: {
            id: resolvedDeliverySlotId,
            type: ResourceType.DeliverySlot,
            meta: {
              ...(existingDeliverySlot?.meta ?? {}),
              core_id: selectedDeliverySlot?.coreSlotId || resolvedDeliverySlotId,
            },
          },
        },
        delivery_day: {
          data: {
            id: resolvedDeliveryDayId,
            type: ResourceType.DeliveryDay,
          },
        },
        ...(resolvedDaySlotLeadTimeId
          ? {
              day_slot_lead_time: {
                data: {
                  id: resolvedDaySlotLeadTimeId,
                  type: ResourceType.DaySlotLeadTime,
                },
              },
            }
          : {}),
      },
    },
    included: updatedIncluded,
  }
}

export const useOrderTileDeliveryDate = ({
  displayedOrder,
  onDeliveryDateSaved,
  order,
  orderDeliveryDetails,
}: UseOrderTileDeliveryDateParams): UseOrderTileDeliveryDateResult => {
  const { trackSaveDeliveryDate } = useOrderTracking()
  const { trackTelemetryForUpdateDeliveryDateError } = useOrderTileTelemetryTracking({
    order: displayedOrder,
  })
  const { isUpdating, updateOrder } = useUpdateDashboardOrder()
  const [hasSaveDeliveryDateError, setHasSaveDeliveryDateError] = React.useState(false)

  useEffect(() => {
    setHasSaveDeliveryDateError(false)
  }, [order])

  const handleSaveDeliveryDate = React.useCallback(
    async ({ date }: { date: string; formattedDate: string }) => {
      if (!orderDeliveryDetails) {
        return
      }

      const selectedDeliveryDay = orderDeliveryDetails.available_delivery_days?.[date]

      if (!selectedDeliveryDay) {
        return
      }

      const orderId = displayedOrder.id
      const newDeliverySlotId = resolveDeliverySlotId({
        displayedOrder,
        selectedDeliveryDate: date,
        selectedDeliveryDay,
      })

      setHasSaveDeliveryDateError(false)

      try {
        const fullOrder = await fetchOrder(orderId)

        await updateOrder({
          orderId,
          body: buildUpdateOrderRequestBody({
            displayedOrder,
            fullOrder,
            selectedDeliveryDay,
            selectedDeliveryDate: date,
          }),
        })

        trackSaveDeliveryDate({
          newDeliverySlotId,
          order: displayedOrder,
        })
        onDeliveryDateSaved?.(date)
      } catch (error) {
        trackTelemetryForUpdateDeliveryDateError({
          error,
          selectedDeliveryDate: date,
        })
        setHasSaveDeliveryDateError(true)
      }
    },
    [
      displayedOrder,
      onDeliveryDateSaved,
      orderDeliveryDetails,
      trackSaveDeliveryDate,
      trackTelemetryForUpdateDeliveryDateError,
      updateOrder,
    ],
  )

  return {
    handleSaveDeliveryDate,
    hasSaveDeliveryDateError,
    isSavingDeliveryDate: isUpdating,
  }
}
